/**
 * Branding panel — site title, tagline, logo, colors, contact, social.
 */
import {useEffect, useState} from 'react';
import type {EditorBrandingPayload, EditorMediaRef} from '../../lib/types';
import MediaPicker from './MediaPicker';

interface Props {
  initial: EditorBrandingPayload;
  onSave: (next: EditorBrandingPayload) => Promise<void>;
  onDirty: () => void;
  saving: boolean;
  siteId: string;
}

const EMPTY: EditorBrandingPayload = {
  site_title: '',
  primary_color: '#1a73e8',
  social_links: {},
};

function normalizeHex(v: string): string {
  if (!v) return '#000000';
  const s = v.trim();
  if (!s.startsWith('#')) return '#' + s.toLowerCase();
  return s.toLowerCase();
}

export default function BrandingPanel({initial, onSave, onDirty, saving, siteId}: Props) {
  const [draft, setDraft] = useState<EditorBrandingPayload>(initial || EMPTY);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial || EMPTY);
  }, [initial]);

  const set = (patch: Partial<EditorBrandingPayload>) => {
    setDraft((d: EditorBrandingPayload) => ({...d, ...patch}));
    onDirty();
  };

  const setSocial = (key: string, value: string) => {
    setDraft((d: EditorBrandingPayload) => ({...d, social_links: {...(d.social_links || {}), [key]: value}}));
    onDirty();
  };

  const removeSocial = (key: string) => {
    setDraft((d: EditorBrandingPayload) => {
      const next = {...(d.social_links || {})};
      delete next[key];
      return {...d, social_links: next};
    });
    onDirty();
  };

  const handleSave = async () => {
    setError(null);
    try {
      await onSave({
        ...draft,
        primary_color: normalizeHex(draft.primary_color),
        secondary_color: draft.secondary_color ? normalizeHex(draft.secondary_color) : null,
        accent_color: draft.accent_color ? normalizeHex(draft.accent_color) : null,
      });
    } catch (e: any) {
      setError(e?.message || '保存失败');
    }
  };

  const socialEntries = Object.entries(draft.social_links || {});

  return (
    <div className="branding-panel">
      <h2>品牌设置</h2>
      {error && <div className="editor-error">{error}</div>}

      <div className="field-grid">
        <label className="field">
          <span className="field-label">站点名称 *</span>
          <input
            type="text"
            value={draft.site_title}
            onChange={(e) => set({site_title: e.target.value})}
            placeholder="例如：梧炅泵业"
          />
        </label>

        <label className="field">
          <span className="field-label">副标题</span>
          <input
            type="text"
            value={draft.tagline || ''}
            onChange={(e) => set({tagline: e.target.value || null})}
            placeholder="一句话介绍"
          />
        </label>

        <label className="field">
          <span className="field-label">公司全称</span>
          <input
            type="text"
            value={draft.company_name || ''}
            onChange={(e) => set({company_name: e.target.value || null})}
            placeholder="例如：梧炅泵业有限公司"
          />
        </label>

        <div className="field">
          <span className="field-label">Logo</span>
          {draft.logo ? (
            <div className="media-preview">
              <img
                src={(draft.logo as any).url || ''}
                alt={draft.logo.alt || 'logo'}
                style={{maxWidth: 120, maxHeight: 80, display: 'block'}}
              />
              <button
                className="btn-link"
                onClick={() => set({logo: null})}
                type="button"
              >
                移除 Logo
              </button>
            </div>
          ) : (
            <button
              className="btn-secondary"
              onClick={() => setPickerOpen(true)}
              type="button"
            >
              + 选择 Logo
            </button>
          )}
        </div>

        <label className="field">
          <span className="field-label">主色 *</span>
          <div className="color-row">
            <input
              type="color"
              value={normalizeHex(draft.primary_color)}
              onChange={(e) => set({primary_color: e.target.value})}
            />
            <input
              type="text"
              value={draft.primary_color}
              onChange={(e) => set({primary_color: e.target.value})}
              placeholder="#1a73e8"
            />
          </div>
        </label>

        <label className="field">
          <span className="field-label">辅色</span>
          <div className="color-row">
            <input
              type="color"
              value={draft.secondary_color ? normalizeHex(draft.secondary_color) : '#cccccc'}
              onChange={(e) => set({secondary_color: e.target.value})}
            />
            <input
              type="text"
              value={draft.secondary_color || ''}
              onChange={(e) => set({secondary_color: e.target.value || null})}
              placeholder="#rrggbb（可选）"
            />
          </div>
        </label>

        <label className="field">
          <span className="field-label">点缀色</span>
          <div className="color-row">
            <input
              type="color"
              value={draft.accent_color ? normalizeHex(draft.accent_color) : '#ff5733'}
              onChange={(e) => set({accent_color: e.target.value})}
            />
            <input
              type="text"
              value={draft.accent_color || ''}
              onChange={(e) => set({accent_color: e.target.value || null})}
              placeholder="#rrggbb（可选）"
            />
          </div>
        </label>

        <label className="field">
          <span className="field-label">联系邮箱</span>
          <input
            type="email"
            value={draft.contact_email || ''}
            onChange={(e) => set({contact_email: e.target.value || null})}
            placeholder="hello@example.com"
          />
        </label>

        <label className="field">
          <span className="field-label">联系电话</span>
          <input
            type="tel"
            value={draft.contact_phone || ''}
            onChange={(e) => set({contact_phone: e.target.value || null})}
            placeholder="+86 138 0000 0000"
          />
        </label>

        <label className="field">
          <span className="field-label">公司地址</span>
          <textarea
            rows={2}
            value={draft.address || ''}
            onChange={(e) => set({address: e.target.value || null})}
            placeholder="例如：上海市浦东新区张江高科园区 1 号"
          />
        </label>
      </div>

      <fieldset className="field-group">
        <legend>社交媒体</legend>
        {socialEntries.map(([k, v]) => (
          <div className="field" key={k}>
            <span className="field-label">{k}</span>
            <div className="row-with-delete">
              <input
                type="url"
                value={v}
                onChange={(e) => setSocial(k, e.target.value)}
              />
              <button
                className="icon-btn danger"
                onClick={() => removeSocial(k)}
                type="button"
              >
                ×
              </button>
            </div>
          </div>
        ))}
        <AddSocialRow onAdd={setSocial} />
      </fieldset>

      <div className="section-editor-actions">
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? '保存中…' : '保存品牌设置'}
        </button>
      </div>

      {pickerOpen && (
        <MediaPicker
          siteId={siteId}
          role="BRAND_LOGO"
          pageId={null}
          sectionId={null}
          onSelect={(m: EditorMediaRef & {url?: string}) => {
            set({logo: {attachment_id: m.attachment_id, alt: m.alt ?? null}});
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
          title="选择 Logo"
        />
      )}
    </div>
  );
}

function AddSocialRow({onAdd}: {onAdd: (k: string, v: string) => void}) {
  const [k, setK] = useState('');
  const [v, setV] = useState('');
  return (
    <div className="row-with-add">
      <input
        type="text"
        placeholder="平台名（例：微博、知乎）"
        value={k}
        onChange={(e) => setK(e.target.value)}
      />
      <input
        type="url"
        placeholder="https://..."
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button
        className="btn-secondary"
        onClick={() => {
          if (k && v) {
            onAdd(k, v);
            setK('');
            setV('');
          }
        }}
        type="button"
      >
        + 添加
      </button>
    </div>
  );
}
